import { useState } from 'react';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, isToday } from 'date-fns';
import { ru } from 'date-fns/locale';

interface DateRangePickerProps {
  value: { from: Date | null; to: Date | null };
  onChange: (range: { from: Date | null; to: Date | null }) => void;
  onTodayClick: () => void;
  onResetClick?: () => void;
}

export function DateRangePicker({ value, onChange, onTodayClick, onResetClick }: DateRangePickerProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selecting, setSelecting] = useState<'from' | 'to'>('from');

  const days = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth)
  });

  const firstDayOfMonth = startOfMonth(currentMonth).getDay();
  const firstDayOffset = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
  const emptyDays = Array(firstDayOffset).fill(null);

  const handleDateClick = (date: Date) => {
    const normalizedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    if (selecting === 'from') {
      onChange({ from: normalizedDate, to: null });
      setSelecting('to');
    } else {
      if (normalizedDate < (value.from || new Date())) {
        onChange({ from: normalizedDate, to: value.from });
      } else {
        onChange({ from: value.from, to: normalizedDate });
      }
      setSelecting('from');
    }
  };

  const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
  const prevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));

  const isInRange = (date: Date) => {
    if (!value.from || !value.to) return false;
    return date >= value.from && date <= value.to;
  };

  return (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
      <div className="flex justify-between items-center mb-4">
        <button onClick={prevMonth} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-700 dark:text-gray-200">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
        </button>
        <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">{format(currentMonth, 'LLLL yyyy', { locale: ru })}</span>
        <button onClick={nextMonth} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-700 dark:text-gray-200">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-2">
        {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map((day) => (
          <div key={day} className="text-center text-sm font-medium text-gray-500 dark:text-gray-400">{day}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {emptyDays.map((_, i) => <div key={`e-${i}`} className="p-2 w-8 h-8" />)}
        {days.map((day) => (
          <button
            key={day.toString()}
            onClick={() => handleDateClick(day)}
            className={`p-2 text-sm rounded-lg w-8 h-8 flex items-center justify-center
              ${!isSameMonth(day, currentMonth) ? 'text-gray-300 dark:text-gray-600' : 'text-gray-700 dark:text-gray-200'}
              ${isToday(day) ? 'bg-blue-100 dark:bg-blue-900/30' : ''}
              ${isInRange(day) ? 'bg-blue-500 text-white' : ''}
              ${value.from && isSameDay(day, value.from) ? 'bg-blue-600 text-white' : ''}
              ${value.to && isSameDay(day, value.to) ? 'bg-blue-600 text-white' : ''}
              hover:bg-gray-100 dark:hover:bg-gray-700`}
          >
            {format(day, 'd')}
          </button>
        ))}
      </div>
      <div className="mt-4 flex justify-between">
        <button onClick={onTodayClick} className="px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md">Сегодня</button>
        <button onClick={onResetClick || (() => onChange({ from: null, to: null }))} className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-md">Сбросить</button>
      </div>
    </div>
  );
}
